import api from "@/lib/api";

const b64ToBytes = (s) => {
    const pad = "=".repeat((4 - (s.length % 4)) % 4);
    const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export const pushPermission = () => ("Notification" in window ? Notification.permission : "unsupported");
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

async function registration() {
    const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/service-worker.js"));
    await navigator.serviceWorker.ready;
    return reg;
}

// Asks permission (if needed), subscribes this device and links it to the signed-in user.
export async function enablePush({ ask = true } = {}) {
    if (!pushSupported()) throw new Error("unsupported");
    let perm = Notification.permission;
    if (perm === "default" && ask) perm = await Notification.requestPermission();
    if (perm !== "granted") throw new Error(perm === "denied" ? "denied" : "dismissed");
    const reg = await registration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        const { data } = await api.get("/push/vapid-key");
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.public_key) });
    }
    await api.post("/push/subscribe", sub.toJSON());
    return true;
}

export async function disablePush() {
    if (!pushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    if (!sub) return;
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch { /* ignore */ }
    await sub.unsubscribe();
}

export async function isPushOn() {
    if (!pushSupported() || Notification.permission !== "granted") return false;
    const reg = await navigator.serviceWorker.getRegistration();
    return !!(reg && (await reg.pushManager.getSubscription()));
}

export const pushErrorText = (e) => ({
    unsupported: isIOS() && !isStandalone() ? "On iPhone, first tap Share → Add to Home Screen, then open HOMIIE from your home screen." : "This browser doesn't support background notifications.",
    denied: "Notifications are blocked. Turn them on in your browser or phone settings for HOMIIE.",
    dismissed: "Notifications weren't enabled.",
}[e?.message] || "Couldn't turn on notifications. Please try again.");
